// Scope-aware broadcast queries. Two endpoints, scope-dispatched at call time:
//   • { kind: 'platform' }                              → /admin/broadcasts
//   • { kind: 'group', groupKind, groupId }             → /groups/{id}/broadcasts
//
// The unified `/groups/{id}/broadcasts` URL covers Circles, Networks, and
// Spaces; the server resolves the row's kind before delegating. `groupKind`
// stays on the scope for cache-key tagging but no longer drives the URL.
//
// Response shape is identical across all scopes; the only thing that differs
// is the row set. Filter-tab counts are exposed through the parallel
// `broadcasts/status-counts` endpoint on each path.

import { queryOptions, useQuery } from '@tanstack/react-query';
import type { components } from '@leanin/api-client';
import type { BroadcastStatus } from '@leanin/shared/broadcast-enums';
import { getApiClient } from '@/lib/api-client';
import { throwApiError } from '@/lib/api-error';

export type { BroadcastStatus };

// The ProseMirror JSON body shape used everywhere a broadcast doc crosses
// the wire. The generated OpenAPI types inline this shape into each request
// and response (no top-level `BroadcastDoc` schema is registered on the
// server because the validators reach into the platform Zod schema
// directly), so we lift the canonical shape off the broadcast detail and
// re-export it as the single web-side reference. Every request and response
// carries the same shape; the server validates all of them against one Zod
// doc.
export type BroadcastDoc = components['schemas']['AdminBroadcastDetail']['bodyDoc'];

// Re-export the rendered preview triple so mutation hooks and components
// share one canonical name for `{ subject, html, text }`.
export type BroadcastPreview = components['schemas']['BroadcastPreview'];

// Re-export the detail row so update / preview hooks expose one canonical
// response type. Platform + group endpoints both return this shape.
export type AdminBroadcastDetail =
  components['schemas']['AdminBroadcastDetail'];

// Prefill payload for the "send a post as a broadcast" flow — the server
// composes subject + bodyDoc from the source post (leader-gated).
export type ComposedBroadcastFromPost =
  components['schemas']['ComposedBroadcastFromPost'];

export type BroadcastGroupKind = 'circle' | 'network' | 'space';

export type BroadcastScope =
  | { readonly kind: 'platform' }
  | {
      readonly kind: 'group';
      readonly groupKind: BroadcastGroupKind;
      readonly groupId: string;
    };

// The group-only member of the scope union. From-post prefill exists only
// under `/groups/{id}/broadcasts` — the platform scope has no source feed.
export type GroupBroadcastScope = Extract<BroadcastScope, { kind: 'group' }>;

// UI-level filter status (adds an 'all' sentinel for the "no filter" tab).
// The platform admin page and every group's email tab share this vocabulary.
export type BroadcastListStatus = 'all' | 'draft' | 'scheduled' | 'sent';

const BROADCAST_LIST_STATUSES: ReadonlyArray<BroadcastListStatus> = [
  'all',
  'draft',
  'scheduled',
  'sent',
];

export function resolveBroadcastListStatus(
  v: unknown,
): BroadcastListStatus | undefined {
  return typeof v === 'string' &&
    (BROADCAST_LIST_STATUSES as ReadonlyArray<string>).includes(v)
    ? (v as BroadcastListStatus)
    : undefined;
}

export interface BroadcastsPageParams {
  readonly status?: BroadcastStatus;
  readonly cursor?: string;
  readonly pageSize?: number;
}

export const broadcastKeys = {
  all: ['broadcasts'] as const,
  // Inner-node prefix per scope (e.g. all lists/detail/status-counts for a
  // given scope). Used by the mutation layer for partial-match invalidation.
  scope: (scope: BroadcastScope) =>
    [...broadcastKeys.all, scopeKey(scope)] as const,

  list: (scope: BroadcastScope, params: BroadcastsPageParams) => {
    const args: BroadcastsPageParams = {
      ...(params.status !== undefined ? { status: params.status } : {}),
      ...(params.cursor !== undefined ? { cursor: params.cursor } : {}),
      ...(params.pageSize !== undefined ? { pageSize: params.pageSize } : {}),
    };
    return queryOptions({
      queryKey: [...broadcastKeys.scope(scope), 'list', args] as const,
      queryFn: () => fetchBroadcasts(scope, args),
    });
  },

  statusCounts: (scope: BroadcastScope) =>
    queryOptions({
      queryKey: [...broadcastKeys.scope(scope), 'status-counts'] as const,
      queryFn: () => fetchBroadcastStatusCounts(scope),
    }),

  detail: (scope: BroadcastScope, id: string) =>
    queryOptions({
      queryKey: [...broadcastKeys.scope(scope), 'detail', id] as const,
      queryFn: () => fetchBroadcastDetail(scope, id),
      enabled: id.length > 0,
    }),

  fromPost: (scope: GroupBroadcastScope, postId: string) =>
    queryOptions({
      queryKey: [...broadcastKeys.scope(scope), 'from-post', postId] as const,
      queryFn: () => fetchBroadcastFromPost(scope, postId),
      enabled: postId.length > 0,
    }),
};

function scopeKey(scope: BroadcastScope): string {
  return scope.kind === 'platform'
    ? 'platform'
    : `${scope.groupKind}:${scope.groupId}`;
}

/**
 * Build a `BroadcastScope` for a group's broadcasts. The canonical place to
 * tag a (kind, groupId) into the discriminated scope union — every consumer
 * (mutation hooks, cache invalidation, preview drivers) routes through this
 * to avoid spelling out the shape inline.
 */
export function groupBroadcastScope(
  kind: BroadcastGroupKind,
  groupId: string,
): GroupBroadcastScope {
  return { kind: 'group', groupKind: kind, groupId };
}

export async function fetchBroadcasts(
  scope: BroadcastScope,
  params: BroadcastsPageParams,
) {
  const client = getApiClient();
  const query = {
    ...(params.cursor !== undefined ? { cursor: params.cursor } : {}),
    ...(params.pageSize !== undefined ? { pageSize: params.pageSize } : {}),
    ...(params.status !== undefined ? { status: params.status } : {}),
  };
  if (scope.kind === 'platform') {
    const { data, error } = await client.GET('/api/v1/admin/broadcasts', {
      params: { query },
    });
    if (data === undefined) throwApiError(error);
    return data;
  }
  const { data, error } = await client.GET(
    '/api/v1/groups/{id}/broadcasts',
    { params: { path: { id: scope.groupId }, query } },
  );
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchBroadcastStatusCounts(scope: BroadcastScope) {
  const client = getApiClient();
  if (scope.kind === 'platform') {
    const { data, error } = await client.GET(
      '/api/v1/admin/broadcasts/status-counts',
      {},
    );
    if (data === undefined) throwApiError(error);
    return data;
  }
  const { data, error } = await client.GET(
    '/api/v1/groups/{id}/broadcasts/status-counts',
    { params: { path: { id: scope.groupId } } },
  );
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchBroadcastDetail(
  scope: BroadcastScope,
  broadcastId: string,
) {
  const client = getApiClient();
  if (scope.kind === 'platform') {
    const { data, error, response } = await client.GET(
      '/api/v1/admin/broadcasts/{id}',
      { params: { path: { id: broadcastId } } },
    );
    if (data === undefined) throwApiError(error, response);
    return data.data;
  }
  const { data, error, response } = await client.GET(
    '/api/v1/groups/{id}/broadcasts/{broadcastId}',
    { params: { path: { id: scope.groupId, broadcastId } } },
  );
  if (data === undefined) throwApiError(error, response);
  return data.data;
}

export function useBroadcasts(
  scope: BroadcastScope,
  params: BroadcastsPageParams,
) {
  return useQuery(broadcastKeys.list(scope, params));
}

export function useBroadcastStatusCounts(scope: BroadcastScope) {
  return useQuery(broadcastKeys.statusCounts(scope));
}

export async function fetchBroadcastFromPost(
  scope: GroupBroadcastScope,
  postId: string,
): Promise<ComposedBroadcastFromPost> {
  const client = getApiClient();
  const { data, error, response } = await client.GET(
    '/api/v1/groups/{id}/broadcasts/from-post/{postId}',
    { params: { path: { id: scope.groupId, postId } } },
  );
  if (data === undefined) throwApiError(error, response);
  return data.data;
}

export function useBroadcastDetail(scope: BroadcastScope, broadcastId: string) {
  return useQuery(broadcastKeys.detail(scope, broadcastId));
}

// `postId` may be empty ('' when the route has no `?fromPost=`) — the
// options' `enabled` gate keeps the query dormant in that case.
export function useBroadcastFromPost(
  scope: GroupBroadcastScope,
  postId: string,
) {
  return useQuery(broadcastKeys.fromPost(scope, postId));
}
